/**
 * SWIPA signed-wallet — клиентский ed25519-ключ (Phase 2 §3).
 *
 * Приватный ключ ГЕНЕРИТСЯ и ЖИВЁТ на клиенте (IndexedDB как CryptoKey), сервер
 * держит только pubkey (ScopeRelay.node). Подпись операций — в браузере.
 *
 * Ключ EXTRACTABLE — этого требует recovery-бэкап (D1): privkey экспортируется в
 * pkcs8, шифруется ключом из пароля и кладётся на сервер. Trade-off: XSS теоретически
 * может выгрузить ключ (цена за backup-ability). Hardening на будущее — хранить
 * non-extractable копию для подписи + отдельный backup при генерации.
 *
 * Совместимость с сервером: WebCrypto Ed25519 экспортит pubkey как SPKI/DER (=hex,
 * который crypto.createPublicKey читает на бэке) и подписывает raw-64-байт ed25519
 * (crypto.verify(null,...) принимает 1:1). Проверено node-roundtrip'ом.
 */

const IDB_NAME = 'swipa-wallet';
const IDB_STORE = 'key';
const idbKey = (userId: string) => `user:${userId}`;

interface StoredKey {
  privateKey: CryptoKey; // ed25519, extractable
  publicKeyHex: string;  // SPKI/DER hex
}

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(userId: string): Promise<StoredKey | null> {
  try {
    const db = await openIDB();
    return await new Promise((resolve) => {
      const req = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(idbKey(userId));
      req.onsuccess = () => resolve((req.result as StoredKey) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function idbSet(userId: string, v: StoredKey): Promise<void> {
  const db = await openIDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(v, idbKey(userId));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function clearWalletKey(userId: string): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(idbKey(userId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch { /* noop */ }
}

// ── hex helpers ───────────────────────────────────────────────────────────────
function bufToHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
export function hexToBuf(hex: string): ArrayBuffer {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out.buffer;
}

// ── key lifecycle ─────────────────────────────────────────────────────────────
/** Загрузить ключ из IDB или сгенерировать новый ed25519 (extractable) и сохранить. */
export async function getOrCreateWalletKey(userId: string): Promise<StoredKey> {
  const existing = await idbGet(userId);
  if (existing?.privateKey && existing.publicKeyHex) return existing;
  const kp = await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']) as CryptoKeyPair;
  const spki = await crypto.subtle.exportKey('spki', kp.publicKey);
  const rec: StoredKey = { privateKey: kp.privateKey, publicKeyHex: bufToHex(spki) };
  await idbSet(userId, rec);
  return rec;
}

export async function hasWalletKey(userId: string): Promise<boolean> {
  return !!(await idbGet(userId));
}

/** Подписать сообщение приватным ключом (raw ed25519 → hex). */
export async function signMessage(privateKey: CryptoKey, message: string): Promise<string> {
  const sig = await crypto.subtle.sign({ name: 'Ed25519' }, privateKey, new TextEncoder().encode(message));
  return bufToHex(sig);
}

/** Экспорт privkey в pkcs8 hex — для зашифрованного recovery-бэкапа (D1). */
export async function exportPrivPkcs8Hex(privateKey: CryptoKey): Promise<string> {
  const pk = await crypto.subtle.exportKey('pkcs8', privateKey);
  return bufToHex(pk);
}

/** Импорт privkey из pkcs8 hex (recovery на новом устройстве) + сохранение в IDB. */
export async function importAndStoreKey(userId: string, pkcs8Hex: string, publicKeyHex: string): Promise<void> {
  const privateKey = await crypto.subtle.importKey('pkcs8', hexToBuf(pkcs8Hex), { name: 'Ed25519' }, true, ['sign']);
  await idbSet(userId, { privateKey, publicKeyHex });
}
